/**
 * LearningRequirementsTab
 * 
 * Catalyst-only tab for viewing and managing training requirements/compliance.
 */

import { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  ClipboardCheck, 
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
  Calendar
} from 'lucide-react';
import { useTechnicianRequirements, useComplianceSummary, useUpdateRequirementStatus } from '@/hooks/useTrainingRequirements';
import { useOpenTrainingWithSession } from '@/hooks/useOpenTrainingWithSession';
import { MobileTrainingFallbackLaunchInterstitial } from '@/components/training/MobileTrainingFallbackLaunchInterstitial';
import { format, isPast, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { RequirementStatus } from '@/types/training';
// Batch 18-G14 — pure display-state helper. Drives the policy-
// badge row and the required-policy reason fragment on each
// requirement card without subscribing this surface to the G4
// ranked-recommendation envelope. See the G13 plan for the
// rationale.
// Batch 18-G16 — extended via an optional third arg to also drive
// the evidence-badge family and the `suppressCompleteDisplay`
// flag. The evidence data is fetched per requirement row by a
// dedicated child component (`LearningRequirementCard` below) that
// composes the existing F32 / F36 evidence-summary hook. See the
// G15 plan for the rationale.
import { buildLearningRequirementDisplayState } from '@/utils/training/learningRequirementDisplayState';
import { clock } from '@/utils/clock';
import { useTechnicianTrainingEvidenceSummaryRows } from '@/hooks/useTechnicianTrainingEvidenceSummaryRows';
import { deriveTechnicianTrainingEvidenceSummaryStatus } from '@/utils/training/technicianEvidenceSummary';
import type { TechnicianRequirementStatus } from '@/types/training';
// Batch 18-I10 — provider evidence summary hook + pure display
// helper. Provider attestation is rendered as a PARALLEL badge family
// next to (never merged into) the existing G14 policy / G16 evidence
// rows. Provider attestation does NOT satisfy the manager-required
// policy and does NOT feed the manager/manual union driven by
// `buildLearningRequirementDisplayState`.
import {
  useTrainingProviderEvidenceSummary,
  type TrainingProviderEvidenceSummaryRow,
} from '@/hooks/useTrainingProviderEvidenceSummary';
import { buildProviderEvidenceDisplayState } from '@/utils/training/providerEvidenceDisplayState';

interface LearningRequirementsTabProps {
  technicianProfileId: string;
}

const STATUS_BADGES: Record<RequirementStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  assigned: { label: 'Assigned', variant: 'outline' },
  in_progress: { label: 'In Progress', variant: 'secondary' },
  completed: { label: 'Completed', variant: 'default' },
  overdue: { label: 'Overdue', variant: 'destructive' },
  waived: { label: 'Waived', variant: 'outline' },
};

export function LearningRequirementsTab({ technicianProfileId }: LearningRequirementsTabProps) {
  const { data: requirements, isLoading } = useTechnicianRequirements(technicianProfileId);
  const { data: summary } = useComplianceSummary(technicianProfileId);
  const updateStatus = useUpdateRequirementStatus();
  const { openTraining } = useOpenTrainingWithSession();

  // Batch 18-F11.12: opt-in mobile fallback affordance, keyed by
  // requirement-instance id. At most one interstitial is open at a
  // time across the list. Toggling is pure UI state and does NOT
  // call openTraining, does NOT call window.open, does NOT create a
  // session row. Launch happens only when the user clicks the
  // interstitial's own Open button, which delegates to
  // handleMobileFallbackLaunch below.
  const [mobileFallbackReqId, setMobileFallbackReqId] = useState<
    string | null
  >(null);
  const toggleMobileFallback = useCallback((reqId: string) => {
    setMobileFallbackReqId((prev) => (prev === reqId ? null : reqId));
  }, []);

  // Batch 18-F11.12: fallback launch handler. Different from
  // handleRequirementLaunch in ONE key way: it does NOT pass
  // `skipWindowOpen: true`, because the interstitial's Open button
  // is not an <a target="_blank"> — there is no native anchor
  // navigation to defer to. The hook therefore performs the single
  // window.open for this path. The launch metadata mirrors the
  // anchor path so the session row + 'opened' event look identical
  // regardless of which entrypoint the technician used.
  const handleMobileFallbackLaunch = useCallback(
    (
      artifact: {
        id: string;
        artifact_type: string;
        estimated_time_minutes: number | null;
        access_url: string | null;
      },
    ) => {
      if (!artifact.access_url) return;
      openTraining({
        technicianProfileId,
        artifactId: artifact.id,
        artifactType: artifact.artifact_type,
        expectedDurationMinutes: artifact.estimated_time_minutes ?? null,
        surface: 'browse',
        source: 'learning_center',
        targetUrl: artifact.access_url,
        launchSource: 'learning_requirements',
      });
    },
    [technicianProfileId, openTraining],
  );

  // Batch 18-E6: route the requirement's "Open" anchor through the
  // shared launch hook so compliance-driven launches also produce a
  // training_consumption_sessions row + 'opened' event. skipWindowOpen
  // because the <a target="_blank"> still handles navigation natively.
  const handleRequirementLaunch = (
    artifact: { id: string; artifact_type: string; estimated_time_minutes: number | null; access_url: string | null },
  ) => {
    if (!artifact.access_url) return;
    openTraining({
      technicianProfileId,
      artifactId: artifact.id,
      artifactType: artifact.artifact_type,
      expectedDurationMinutes: artifact.estimated_time_minutes ?? null,
      surface: 'browse',
      source: 'learning_center',
      targetUrl: artifact.access_url,
      launchSource: 'learning_requirements',
      skipWindowOpen: true,
    });
  };

  const handleMarkInProgress = async (requirementId: string) => {
    try {
      await updateStatus.mutateAsync({
        requirementId,
        technicianProfileId,
        status: 'in_progress',
      });
      toast.success('Marked as in progress');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkComplete = async (requirementId: string) => {
    try {
      await updateStatus.mutateAsync({
        requirementId,
        technicianProfileId,
        status: 'completed',
      });
      toast.success('Marked as complete!');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Batch 18-I10 — single batched provider evidence summary query
  // over all visible requirement artifact ids. Rows are grouped by
  // `matched_artifact_id` so each `LearningRequirementCard` receives
  // only the provider rows for its own artifact. The hook gates
  // itself on technicianProfileId + non-empty artifactIds; we pass an
  // explicit `enabled` for clarity. Hooks must run unconditionally
  // before the loading / empty-list early returns below.
  const requirementArtifactIds = (requirements ?? [])
    .map((req) => req.requirement?.artifact_id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  const providerEvidenceSummary = useTrainingProviderEvidenceSummary({
    technicianProfileId,
    artifactIds: requirementArtifactIds,
    enabled: Boolean(
      technicianProfileId && requirementArtifactIds.length > 0,
    ),
  });
  const providerEvidenceRows = providerEvidenceSummary.data ?? [];
  const providerRowsByArtifactId = new Map<
    string,
    TrainingProviderEvidenceSummaryRow[]
  >();
  for (const row of providerEvidenceRows) {
    const existing = providerRowsByArtifactId.get(row.matched_artifact_id);
    if (existing) {
      existing.push(row);
    } else {
      providerRowsByArtifactId.set(row.matched_artifact_id, [row]);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!requirements || requirements.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="font-medium">No training requirements</p>
        <p className="text-sm mt-1">You're all caught up! No assigned training yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compliance Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Compliance Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Compliance Rate</span>
            <span className="text-lg font-bold">{summary?.complianceRate || 0}%</span>
          </div>
          <Progress value={summary?.complianceRate || 0} className="h-2" />
          
          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="text-center p-2 rounded bg-muted/50">
              <p className="text-lg font-bold text-green-600">{summary?.completed || 0}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="text-center p-2 rounded bg-muted/50">
              <p className="text-lg font-bold text-blue-600">{summary?.inProgress || 0}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div className="text-center p-2 rounded bg-muted/50">
              <p className="text-lg font-bold text-destructive">{summary?.overdue || 0}</p>
              <p className="text-xs text-muted-foreground">Overdue</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requirements List */}
      <div className="space-y-3">
        {/* Batch 18-G16 — per-row child component hosts the
            evidence-summary hook call so the parent's `.map` body
            stays shallow and so React only re-fetches evidence
            for rows whose artifact id actually changes. */}
        {requirements.map((req) => {
          // Batch 18-I10 — per-row provider rows looked up by the
          // requirement's `artifact_id`. Empty array when the
          // requirement has no artifact or no provider evidence.
          const artifactIdForRow = req.requirement?.artifact_id ?? null;
          const providerRowsForRow = artifactIdForRow
            ? providerRowsByArtifactId.get(artifactIdForRow) ?? []
            : [];
          return (
            <LearningRequirementCard
              key={req.id}
              req={req}
              technicianProfileId={technicianProfileId}
              providerEvidenceRows={providerRowsForRow}
              mobileFallbackOpen={mobileFallbackReqId === req.id}
              onToggleMobileFallback={() => toggleMobileFallback(req.id)}
              onCloseMobileFallback={() => setMobileFallbackReqId(null)}
              onLaunchAnchor={handleRequirementLaunch}
              onLaunchMobileFallback={handleMobileFallbackLaunch}
              onMarkInProgress={() => handleMarkInProgress(req.requirement_id)}
              onMarkComplete={() => handleMarkComplete(req.requirement_id)}
              updatePending={updateStatus.isPending}
            />
          );
        })}
      </div>
    </div>
  );
}

// =============================================
// Batch 18-G16 — per-requirement card.
//
// Owns the per-row evidence-summary hook call so the parent
// `LearningRequirementsTab` stays simple and so each row's evidence
// fetch is independent (RLS-scoped, cached by React Query). When
// evidence is loading or errored the card still renders the G14
// policy badges + reason fragment — the evidence-badge block is a
// progressive enhancement, never a blocker.
// =============================================

interface LearningRequirementCardProps {
  req: TechnicianRequirementStatus;
  technicianProfileId: string;
  // Batch 18-I10 — provider evidence rows for this requirement's
  // artifact id only, sliced from the parent's
  // `providerRowsByArtifactId` map. Always defined (empty array when
  // no provider evidence exists).
  providerEvidenceRows: TrainingProviderEvidenceSummaryRow[];
  mobileFallbackOpen: boolean;
  onToggleMobileFallback: () => void;
  onCloseMobileFallback: () => void;
  onLaunchAnchor: (artifact: {
    id: string;
    artifact_type: string;
    estimated_time_minutes: number | null;
    access_url: string | null;
  }) => void;
  onLaunchMobileFallback: (artifact: {
    id: string;
    artifact_type: string;
    estimated_time_minutes: number | null;
    access_url: string | null;
  }) => void;
  onMarkInProgress: () => void;
  onMarkComplete: () => void;
  updatePending: boolean;
}

function LearningRequirementCard({
  req,
  technicianProfileId,
  providerEvidenceRows,
  mobileFallbackOpen,
  onToggleMobileFallback,
  onCloseMobileFallback,
  onLaunchAnchor,
  onLaunchMobileFallback,
  onMarkInProgress,
  onMarkComplete,
  updatePending,
}: LearningRequirementCardProps) {
  const requirement = req.requirement;
  const artifact = requirement?.artifact;
  const dueDate = requirement?.required_by_date;
  const isOverdue =
    dueDate &&
    isPast(parseISO(dueDate)) &&
    req.status !== 'completed' &&
    req.status !== 'waived';
  const status = isOverdue ? 'overdue' : req.status;
  const statusInfo =
    STATUS_BADGES[status as RequirementStatus] || STATUS_BADGES.assigned;

  // Batch 18-G16 — per-card evidence summary. The hook is RLS-
  // scoped to a single (technician, artifact) pair and caches by
  // React Query, so re-mounts across tab visits are cheap. While
  // the hook is loading or errored, we omit the evidence summary
  // and the helper returns G14 baseline (no evidence badge, no
  // suppression).
  const artifactId = requirement?.artifact_id ?? artifact?.id ?? null;
  const evidence = useTechnicianTrainingEvidenceSummaryRows({
    technicianProfileId,
    artifactId: artifactId ?? '',
    enabled: Boolean(technicianProfileId && artifactId),
  });
  const evidenceReady =
    Boolean(artifactId) && !evidence.isLoading && !evidence.error;
  const evidenceStatus = evidenceReady
    ? deriveTechnicianTrainingEvidenceSummaryStatus(
        evidence.rows,
        evidence.requiresManagerVerification ??
          requirement?.requires_manager_verification ??
          false,
      )
    : null;

  const displayState = buildLearningRequirementDisplayState(
    req,
    clock.now(),
    evidenceStatus !== null ? { status: evidenceStatus } : undefined,
  );

  // Batch 18-I10 — provider evidence display state at the
  // 'requirement' surface. The helper hides pending / superseded /
  // none on this surface; only attested + revoked produce badges.
  // Provider state is NEVER fed into `buildLearningRequirementDisplayState`
  // above, so `Verification pending` / `Verified by manager` /
  // `Verification revoked` continue to be driven exclusively by the
  // F36 manager/manual evidence summary.
  const providerEvidenceDisplay = buildProviderEvidenceDisplayState(
    providerEvidenceRows,
    'requirement',
  );
  const providerRowByEventId = new Map(
    providerEvidenceRows.map(
      (row) => [row.latest_event_id, row] as const,
    ),
  );

  // Batch 18-G16 — when evidence is revoked, the primary `Completed`
  // badge is suppressed so a revoked row never appears trusted.
  // The flag is helper-driven so this stays a pure UI decision —
  // no row-status mutation.
  const hideCompletedPrimary =
    displayState.visibility.suppressCompleteDisplay && status === 'completed';

  return (
    <Card
      className={cn(
        'transition-colors',
        status === 'overdue' && 'border-destructive/50 bg-destructive/5',
        status === 'completed' &&
          !displayState.visibility.suppressCompleteDisplay &&
          'border-green-500/30 bg-green-500/5',
      )}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {!hideCompletedPrimary && (
                <Badge variant={statusInfo.variant}>
                  {status === 'completed' && (
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                  )}
                  {status === 'overdue' && (
                    <AlertCircle className="h-3 w-3 mr-1" />
                  )}
                  {status === 'in_progress' && (
                    <Clock className="h-3 w-3 mr-1" />
                  )}
                  {statusInfo.label}
                </Badge>
              )}
              {requirement?.domain_key && (
                <Badge variant="outline" className="text-xs">
                  {requirement.domain_key.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>

            {/* Batch 18-G14 — G4-aligned policy badge row.
                Rendered next to the primary status/domain
                chips so they read as a single policy block.
                Empty render when no policy badges fire
                (e.g. waived rows). */}
            {displayState.policyBadges.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-1 mb-2"
                data-testid="learning-requirement-policy-badges"
              >
                {displayState.policyBadges.map((badge) => (
                  <Badge
                    key={badge.testId}
                    variant={badge.variant}
                    className="text-xs"
                    data-testid={badge.testId}
                  >
                    {badge.label}
                  </Badge>
                ))}
              </div>
            )}

            {/* Batch 18-G16 — G4-aligned evidence-badge family.
                Visually + semantically separate from the policy
                badge row above. Never renders for waived rows or
                when evidence is loading / errored / no_evidence. */}
            {displayState.evidenceBadges.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-1 mb-2"
                data-testid="learning-requirement-evidence-badges"
              >
                {displayState.evidenceBadges.map((badge) => (
                  <Badge
                    key={badge.testId}
                    variant={badge.variant}
                    className="text-xs"
                    data-testid={badge.testId}
                  >
                    {badge.label}
                  </Badge>
                ))}
              </div>
            )}

            {/* Batch 18-I10 — provider evidence badge family.
                Sibling to the G14 policy row and the G16 evidence
                row, NEVER merged into either. The I8 helper at
                surface 'requirement' emits badges only for attested
                / revoked; pending / superseded / none never render
                here. Provider attestation NEVER satisfies the
                manager-required policy — the F36/G16 manager
                pending row above keeps rendering independently. */}
            {providerEvidenceDisplay.evidenceBadges.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-1 mb-2"
                data-testid="learning-requirement-provider-evidence-badges"
              >
                {providerEvidenceDisplay.evidenceBadges.map((badge) => {
                  const row = badge.latestEventId
                    ? providerRowByEventId.get(badge.latestEventId)
                    : undefined;
                  return (
                    <span
                      key={badge.latestEventId ?? badge.testId}
                      className="inline-flex items-center gap-1"
                    >
                      {(badge.providerDisplayName || row?.provider_key) && (
                        <span
                          className="text-xs text-muted-foreground"
                          data-testid="learning-requirement-provider-evidence-provider-name"
                        >
                          {badge.providerDisplayName ?? row?.provider_key}
                        </span>
                      )}
                      <Badge
                        variant={badge.variant}
                        className="text-xs"
                        data-testid={badge.testId}
                      >
                        {badge.label}
                      </Badge>
                    </span>
                  );
                })}
              </div>
            )}

            <h4 className="font-medium text-sm">
              {artifact?.title ||
                `Training Requirement: ${requirement?.domain_key || 'General'}`}
            </h4>

            {artifact?.provider_name && (
              <p className="text-xs text-muted-foreground mt-1">
                {artifact.provider_name}
              </p>
            )}

            {dueDate && (
              <p
                className={cn(
                  'text-xs mt-2 flex items-center gap-1',
                  isOverdue ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                <Calendar className="h-3 w-3" />
                Due: {format(parseISO(dueDate), 'MMM d, yyyy')}
              </p>
            )}

            {req.status === 'waived' && req.waived_reason && (
              <p className="text-xs text-muted-foreground mt-2 italic">
                Waived: {req.waived_reason}
              </p>
            )}

            {/* Batch 18-G14 — G4-aligned required-policy
                reason fragment. Mirrors G8's
                `training-card-ranked-policy-reason` line
                on the ticket surface. Never renders for
                waived / completed rows; vocabulary is
                bounded by the helper. */}
            {displayState.reasonFragment && (
              <p
                className="text-xs text-muted-foreground mt-2"
                data-testid="learning-requirement-reason-fragment"
              >
                {displayState.reasonFragment}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0">
            {artifact?.access_url && (
              <Button size="sm" variant="outline" asChild>
                <a
                  href={artifact.access_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onLaunchAnchor(artifact as any)}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Open
                </a>
              </Button>
            )}

            {req.status === 'assigned' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={onMarkInProgress}
                disabled={updatePending}
              >
                Start
              </Button>
            )}

            {(req.status === 'in_progress' || req.status === 'assigned') && (
              <Button
                size="sm"
                onClick={onMarkComplete}
                disabled={updatePending}
              >
                Complete
              </Button>
            )}
          </div>
        </div>

        {/* Batch 18-F11.12: opt-in mobile fallback affordance.
            Only shown when the requirement has a usable
            artifact + access_url (same gate as the primary
            anchor above). The toggle is a ghost-variant
            secondary; clicking it never auto-opens the
            provider URL and never calls openTraining. The
            launch only happens after the user clicks the
            interstitial's own Open button, which delegates to
            onLaunchMobileFallback — which intentionally
            does NOT pass skipWindowOpen=true (no anchor on
            this path; the hook performs window.open). */}
        {artifact?.access_url && (
          <div className="mt-3 pt-3 border-t space-y-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={onToggleMobileFallback}
              data-testid={`requirements-mobile-fallback-toggle-${req.id}`}
            >
              {mobileFallbackOpen
                ? 'Hide mobile fallback'
                : 'Open with mobile fallback'}
            </Button>
            {mobileFallbackOpen && (
              <MobileTrainingFallbackLaunchInterstitial
                trainingTitle={artifact.title}
                providerName={artifact.provider_name}
                providerUrl={artifact.access_url ?? ''}
                estimatedDurationMinutes={
                  artifact.estimated_time_minutes ?? null
                }
                onOpenProvider={() => onLaunchMobileFallback(artifact as any)}
                onCancel={onCloseMobileFallback}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
